import Phaser from 'phaser';
import { PHYSICS } from '../config';
import { CollisionGrid, AABB } from '../engine/CollisionGrid';

export interface PlayerState {
  x: number;
  y: number;
  velocityX: number;
  velocityY: number;
  onGround: boolean;
  facingRight: boolean;
  isJumping: boolean;
  isPogo: boolean;
}

/**
 * Player controller with Keen-style physics and sprite animation
 */
export class Player {
  // Position and velocity
  private x: number = 0;
  private y: number = 0;
  private prevX: number = 0;
  private prevY: number = 0;
  private velocityX: number = 0;
  private velocityY: number = 0;

  // State
  private onGround: boolean = false;
  private facingRight: boolean = true;
  private isJumping: boolean = false;
  private isPogo: boolean = false;
  private jumpHeld: boolean = false;
  private isRunning: boolean = false;

  // Timers (in ms)
  private coyoteTimer: number = 0;
  private jumpBufferTimer: number = 0;
  private animTimer: number = 0;
  private animFrame: number = 0;

  // Hitbox dimensions
  private width: number = 12;
  private height: number = 14;

  // Sprite
  private sprite: Phaser.GameObjects.Sprite | null = null;

  // Collision reference
  private collisionGrid: CollisionGrid | null = null;

  // Input state
  private inputLeft: boolean = false;
  private inputRight: boolean = false;
  private inputJump: boolean = false;
  private inputJumpPressed: boolean = false;
  private inputPogo: boolean = false;

  // Stats
  private score: number = 0;
  private lives: number = 3;
  private ammo: number = 10;
  private keys: { yellow: boolean; red: boolean; blue: boolean } = {
    yellow: false,
    red: false,
    blue: false,
  };

  constructor() {}

  init(scene: Phaser.Scene, collisionGrid: CollisionGrid, startX: number, startY: number): void {
    this.collisionGrid = collisionGrid;
    this.x = startX;
    this.y = startY;
    this.prevX = startX;
    this.prevY = startY;

    // Create sprite
    this.sprite = scene.add.sprite(startX, startY, 'player');
    this.sprite.setOrigin(0, 0);
  }

  setInput(left: boolean, right: boolean, jump: boolean, jumpPressed: boolean, pogo: boolean): void {
    this.inputLeft = left;
    this.inputRight = right;
    this.inputJump = jump;
    this.inputJumpPressed = jumpPressed;
    this.inputPogo = pogo;
  }

  fixedUpdate(dt: number): void {
    if (!this.collisionGrid) return;

    this.prevX = this.x;
    this.prevY = this.y;

    const dtSec = dt / 1000;

    this.updateTimers(dt);
    this.handleHorizontalMovement(dtSec);
    this.handleJumping();
    this.applyGravity(dtSec);

    this.x += this.velocityX * dtSec;
    this.y += this.velocityY * dtSec;

    this.resolveCollisions();
    this.updateGroundState();
    this.updateAnimation(dt);
  }

  private updateTimers(dt: number): void {
    if (!this.onGround && this.coyoteTimer > 0) {
      this.coyoteTimer -= dt;
    }

    if (this.jumpBufferTimer > 0) {
      this.jumpBufferTimer -= dt;
    }

    if (this.inputJumpPressed) {
      this.jumpBufferTimer = PHYSICS.JUMP_BUFFER_MS;
    }
  }

  private handleHorizontalMovement(dtSec: number): void {
    const friction = this.onGround ? PHYSICS.GROUND_FRICTION : PHYSICS.AIR_FRICTION;

    this.isRunning = false;

    if (this.inputLeft) {
      this.velocityX -= PHYSICS.ACCELERATION * dtSec;
      this.facingRight = false;
      this.isRunning = true;
    } else if (this.inputRight) {
      this.velocityX += PHYSICS.ACCELERATION * dtSec;
      this.facingRight = true;
      this.isRunning = true;
    } else {
      if (this.velocityX > 0) {
        this.velocityX = Math.max(0, this.velocityX - friction * dtSec);
      } else if (this.velocityX < 0) {
        this.velocityX = Math.min(0, this.velocityX + friction * dtSec);
      }
    }

    this.velocityX = Phaser.Math.Clamp(this.velocityX, -PHYSICS.MAX_SPEED, PHYSICS.MAX_SPEED);
  }

  private handleJumping(): void {
    const canJump = this.onGround || this.coyoteTimer > 0;
    const wantsToJump = this.jumpBufferTimer > 0;

    if (canJump && wantsToJump && !this.isJumping) {
      this.velocityY = PHYSICS.JUMP_VELOCITY;
      this.isJumping = true;
      this.jumpHeld = true;
      this.coyoteTimer = 0;
      this.jumpBufferTimer = 0;
      this.onGround = false;
    }

    if (this.isJumping && this.jumpHeld && !this.inputJump && this.velocityY < 0) {
      this.velocityY *= PHYSICS.VARIABLE_JUMP_MULTIPLIER;
      this.jumpHeld = false;
    }

    if (this.inputPogo && this.onGround) {
      this.velocityY = PHYSICS.POGO_VELOCITY;
      this.isPogo = true;
      this.onGround = false;
    }
  }

  private applyGravity(dtSec: number): void {
    this.velocityY += PHYSICS.GRAVITY * dtSec;
    this.velocityY = Math.min(this.velocityY, PHYSICS.MAX_FALL_SPEED);
  }

  private resolveCollisions(): void {
    if (!this.collisionGrid) return;

    const box: AABB = {
      x: this.x,
      y: this.y,
      width: this.width,
      height: this.height,
    };

    // Horizontal
    const hResult = this.collisionGrid.resolveCollision(
      { ...box, y: this.prevY },
      this.velocityX,
      0
    );

    if (hResult.collided) {
      this.x += hResult.pushX;
      if (hResult.onLeftWall || hResult.onRightWall) {
        this.velocityX = 0;
      }
    }

    // Vertical
    box.x = this.x;
    box.y = this.y;
    const vResult = this.collisionGrid.resolveCollision(box, 0, this.velocityY);

    if (vResult.collided) {
      this.y += vResult.pushY;
      if (vResult.onGround) {
        this.velocityY = 0;
        this.onGround = true;
        this.isJumping = false;
        this.isPogo = false;
        this.coyoteTimer = PHYSICS.COYOTE_TIME_MS;
      }
      if (vResult.onCeiling) {
        this.velocityY = 0;
      }
    }
  }

  private updateGroundState(): void {
    if (!this.collisionGrid) return;

    if (this.onGround) {
      const checkBox: AABB = {
        x: this.x,
        y: this.y + 1,
        width: this.width,
        height: this.height,
      };
      const result = this.collisionGrid.resolveCollision(checkBox, 0, 1);
      if (!result.onGround) {
        this.onGround = false;
        this.coyoteTimer = PHYSICS.COYOTE_TIME_MS;
      }
    }
  }

  private updateAnimation(dt: number): void {
    this.animTimer += dt;

    let targetTexture = 'player';

    if (!this.onGround) {
      targetTexture = 'playerJump';
    } else if (this.isRunning && Math.abs(this.velocityX) > 10) {
      // Running animation
      if (this.animTimer > 100) {
        this.animTimer = 0;
        this.animFrame = (this.animFrame + 1) % 2;
      }
      targetTexture = this.animFrame === 0 ? 'playerRun1' : 'playerRun2';
    } else {
      targetTexture = 'player';
    }

    if (this.sprite && this.sprite.texture.key !== targetTexture) {
      this.sprite.setTexture(targetTexture);
    }
  }

  render(alpha: number): void {
    if (!this.sprite) return;

    const renderX = this.prevX + (this.x - this.prevX) * alpha;
    const renderY = this.prevY + (this.y - this.prevY) * alpha;

    this.sprite.x = renderX;
    this.sprite.y = renderY;
    this.sprite.setFlipX(!this.facingRight);
  }

  getHitbox(): AABB {
    return {
      x: this.x,
      y: this.y,
      width: this.width,
      height: this.height,
    };
  }

  getPosition(): { x: number; y: number } {
    return { x: this.x, y: this.y };
  }

  // Stats methods
  addScore(points: number): void {
    this.score += points;
  }

  getScore(): number {
    return this.score;
  }

  addAmmo(amount: number): void {
    this.ammo += amount;
  }

  getAmmo(): number {
    return this.ammo;
  }

  addKey(color: 'yellow' | 'red' | 'blue'): void {
    this.keys[color] = true;
  }

  hasKey(color: 'yellow' | 'red' | 'blue'): boolean {
    return this.keys[color];
  }

  getLives(): number {
    return this.lives;
  }

  setPosition(x: number, y: number): void {
    this.x = x;
    this.y = y;
    this.prevX = x;
    this.prevY = y;
    this.velocityX = 0;
    this.velocityY = 0;
  }

  getState(): PlayerState {
    return {
      x: this.x,
      y: this.y,
      velocityX: this.velocityX,
      velocityY: this.velocityY,
      onGround: this.onGround,
      facingRight: this.facingRight,
      isJumping: this.isJumping,
      isPogo: this.isPogo,
    };
  }
}
